import type { ExecTransmittal } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, toast } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

const today = () => new Date().toISOString().slice(0, 10);

function Receive({ t, onDone }: { t: ExecTransmittal; onDone: () => void }) {
  const [f, setF] = useState({ receivedOn: today(), receivedBy: '' });
  const m = useMutation({
    mutationFn: () => api(`/documents/exec/transmittals/${t.id}/receive`, { body: f }),
    onSuccess: () => {
      toast('Получение отмечено');
      onDone();
    },
  });
  return (
    <div className="mt-2 grid gap-3 rounded-md bg-tile p-3 print:hidden">
      <Field label="Дата получения" htmlFor={`tr-rd-${t.id}`}>
        <Input
          id={`tr-rd-${t.id}`}
          type="date"
          className="!h-11"
          value={f.receivedOn}
          min={t.sentOn}
          max={today()}
          onChange={(e) => setF({ ...f, receivedOn: e.target.value })}
        />
      </Field>
      <Field label="Кто принял" htmlFor={`tr-rb-${t.id}`}>
        <Input
          id={`tr-rb-${t.id}`}
          className="!h-11"
          value={f.receivedBy}
          maxLength={200}
          onChange={(e) => setF({ ...f, receivedBy: e.target.value })}
        />
      </Field>
      {m.error ? <ErrorBox error={m.error} /> : null}
      <div>
        <Button
          variant="primary"
          className="min-h-11"
          disabled={f.receivedBy.trim().length < 2 || !f.receivedOn}
          loading={m.isPending}
          onClick={() => m.mutate()}
        >
          Подтвердить получение
        </Button>
      </div>
    </div>
  );
}

/** Передача пакета по сопроводительной накладной (ДК-09) */
export function ExecTransmittals({ projectId, objectId }: { projectId: string; objectId?: string }) {
  const qc = useQueryClient();
  const key = ['exec-transmittals', projectId];
  const q = useQuery({
    queryKey: key,
    queryFn: () =>
      api<{ items: ExecTransmittal[]; canEdit: boolean }>(
        `/documents/exec/projects/${projectId}/transmittals`,
      ),
    retry: false,
  });
  const [form, setForm] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [f, setF] = useState({ recipient: '', sentOn: today(), note: '' });
  const create = useMutation({
    mutationFn: () =>
      api(`/documents/exec/projects/${projectId}/transmittals`, {
        body: { recipient: f.recipient, sentOn: f.sentOn, note: f.note || null, objectId: objectId ?? null },
      }),
    onSuccess: () => {
      toast('Передача оформлена');
      setForm(false);
      setF({ recipient: '', sentOn: today(), note: '' });
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  const items = q.data?.items ?? [];
  return (
    <Panel title="Передача пакета">
      <div className="grid gap-3">
        <p className="text-sm text-ink-2">
          Сопроводительная накладная: кому и когда передан пакет, какие документы и файлы ушли, когда получен.
        </p>
        {q.data?.canEdit ? (
          <div className="print:hidden">
            <Button className="min-h-11" onClick={() => setForm(!form)}>
              {form ? 'Скрыть форму' : 'Оформить передачу'}
            </Button>
          </div>
        ) : null}
        {form ? (
          <div className="grid gap-3 rounded-md border border-line p-3 print:hidden">
            <Field label="Кому передано" htmlFor="tr-recipient" hint="Заказчик, технадзор, комиссия">
              <Input
                id="tr-recipient"
                className="!h-11"
                value={f.recipient}
                maxLength={200}
                onChange={(e) => setF({ ...f, recipient: e.target.value })}
              />
            </Field>
            <Field label="Дата передачи" htmlFor="tr-sent">
              <Input
                id="tr-sent"
                type="date"
                className="!h-11"
                value={f.sentOn}
                max={today()}
                onChange={(e) => setF({ ...f, sentOn: e.target.value })}
              />
            </Field>
            <Field label="Примечание" htmlFor="tr-note">
              <Textarea
                id="tr-note"
                rows={2}
                value={f.note}
                maxLength={1000}
                onChange={(e) => setF({ ...f, note: e.target.value })}
              />
            </Field>
            <p className="text-sm text-ink-2">
              В накладную попадут все готовые документы описи{objectId ? ' выбранного объекта' : ''}.
            </p>
            {create.error ? <ErrorBox error={create.error} /> : null}
            <div>
              <Button
                variant="primary"
                className="min-h-11"
                disabled={f.recipient.trim().length < 2 || !f.sentOn}
                loading={create.isPending}
                onClick={() => create.mutate()}
              >
                Оформить
              </Button>
            </div>
          </div>
        ) : null}
        {q.error ? <ErrorBox error={q.error} /> : null}
        {q.data && !items.length ? <EmptyState title="Передач пока не было." /> : null}
        <ul className="grid gap-2">
          {items.map((t) => (
            <li key={t.id} className="rounded-md border border-line p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{t.numberLabel}</span>
                <Badge tone={t.status === 'received' ? 'success' : 'warning'}>
                  {t.status === 'received' ? 'Получено' : 'Ждём подтверждения'}
                </Badge>
              </div>
              <p className="[overflow-wrap:anywhere]">
                {t.recipient} · передано {date(t.sentOn)}
                {t.status === 'received'
                  ? ` · получено ${date(t.receivedOn)}, принял ${t.receivedBy}`
                  : t.waitingDays
                    ? ` · ждём ${t.waitingDays} дн.`
                    : ''}
              </p>
              {t.note ? <p className="text-ink-2 [overflow-wrap:anywhere]">{t.note}</p> : null}
              <button
                type="button"
                className="mt-1 inline-flex min-h-11 items-center underline"
                onClick={() => setOpenId(openId === t.id ? null : t.id)}
              >
                {openId === t.id ? 'Скрыть состав' : `Состав: ${t.entries.length} док.`}
              </button>
              {openId === t.id ? (
                <ol className="grid gap-1">
                  {t.entries.map((e) => (
                    <li key={e.itemId} className="[overflow-wrap:anywhere]">
                      {e.no}. {e.title}
                      {e.files.length ? ` — ${e.files.map((x) => x.name).join(', ')}` : ''}
                    </li>
                  ))}
                </ol>
              ) : null}
              {t.status === 'sent' && q.data?.canEdit ? (
                <Receive
                  t={t}
                  onDone={() => {
                    void qc.invalidateQueries({ queryKey: key });
                    void qc.invalidateQueries({ queryKey: ['inbox'] });
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
